/**
 * Shared route tree for the Vite shell and the Next.js adapter.
 *
 * Route discovery is explicit in routeModules.ts. Keeping the registry as
 * ordinary dynamic imports removes the Vite-only import.meta.glob boundary.
 */
import React from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { createLazyRoute } from '../createLazyRoute';
import { routeModules } from './routeModules';
import AuthenticatedLayout from './__layout';

type LayoutRoute = RouteObject & { children?: RouteObject[] };

const fallback = <div data-route-fallback aria-hidden="true" />;

function addOrReplaceRoute(routes: LayoutRoute[], route: LayoutRoute, byPath: Map<string, LayoutRoute>): void {
  const existing = byPath.get(route.path ?? '');
  if (existing?.children) {
    existing.lazy = route.lazy;
    return;
  }
  routes.push(route);
  if (route.path) byPath.set(route.path, route);
}

function buildChildren(): RouteObject[] {
  const children: LayoutRoute[] = [];
  const byPath = new Map<string, LayoutRoute>();

  for (const module of routeModules) {
    const lazy = createLazyRoute(module.load, fallback);

    if (module.index) {
      if (!module.path) {
        children.push({ index: true, lazy });
        continue;
      }
      const parent = byPath.get(module.path) ?? { path: module.path, children: [] };
      if (!byPath.has(module.path)) {
        byPath.set(module.path, parent);
        children.push(parent);
      }
      parent.children?.push({ index: true, lazy });
      continue;
    }

    const slash = module.path.indexOf('/');
    if (slash > 0) {
      const parentPath = module.path.slice(0, slash);
      const parent = byPath.get(parentPath);
      if (parent?.children) {
        parent.children.push({ path: module.path.slice(slash + 1), lazy });
        continue;
      }
    }

    addOrReplaceRoute(children, { path: module.path, lazy }, byPath);
  }

  children.push({ path: '*', element: <Navigate to="/home" replace /> });
  return children;
}

export const routeTree: RouteObject[] = [{
  path: '/',
  element: React.createElement(AuthenticatedLayout),
  children: buildChildren(),
}];

/** /login remains outside the authenticated mobile shell. */
export const loginRoute: RouteObject = {
  path: '/login',
  lazy: createLazyRoute(() => import('./login'), fallback),
};
